



































































































/* global google */
import { defineComponent, onMounted, ref, useContext, watch, computed, PropType } from '@nuxtjs/composition-api'

import Coordinate from '@/interfaces/Coordinate'

import StoreCars from '@/classes/StoreCars'
import Store from '@/classes/Store'
import HertzStoreCars from '@/classes/Hertz/HertzStoreCars'
import HertzStore from '@/classes/Hertz/HertzStore'

import useCheckCarSource from '@/composables/use-check-car-source'
import CarSource from '@/enums/CarSource'

import ggoLogger from '@/modules/ggo-logger'
import { LogName } from '@/interfaces/Logger'
import * as _ from 'lodash'

export default defineComponent({
  props: {
    storeCarsList: {
      type: Array as PropType<Array<StoreCars | HertzStoreCars>>,
      default: [],
    },
    store: {
      type: Store as PropType<Store | HertzStore | null>,
      default: null,
    },
    origin: {
      type: Object as PropType<Coordinate | null>,
      default: null,
    },
    mode: {
      type: String as PropType<'window' | 'drawer'>,
      default: 'window',
    },
    mapOptions: {
      type: Object as PropType<google.maps.MapOptions | null>,
      default: null,
    },
    hoverStoreId: {
      type: [Number, String] as PropType<number | string | null>,
      default: null,
    },
    activeStoreId: {
      type: [Number, String] as PropType<number | string | null>,
      default: null,
    },
    activeStoreLocationCode: {
      type: String as PropType<string | null>,
      default: null,
    },
    pickReturnOrNot: {
      type: String || null,
      default: null,
    },
  },
  setup (props, { emit }) {
    const el = ref<Element | null>(null)
    const map = ref<google.maps.Map | null>(null)
    const onInit = (_map: google.maps.Map | null, _el: Element) => {
      map.value = _map
      el.value = _el

      map.value?.addListener('click', clearActive)
    }

    const { query } = useContext()

    const carSource = useCheckCarSource()
    const returnLocation = computed(() => query.value.returnLocation)

    watch(() => props.pickReturnOrNot, (val) => {
      showWindowReturnOrNot.value = val
      if (val === 'rent') {
        activeStoreCars.value = filteredStoreCarsList.value.find(({ store }) => {
          if (!store) return false
          return props.activeStoreLocationCode === (store as HertzStore).locationCode
        }) ?? null
      } else {
        activeStoreCars.value = filteredStoreCarsList.value.find(({ store }) => {
          if (!store) return false
          return props.activeStoreLocationCode === (store as HertzStore).endLocationCode
        }) ?? null
      }
      if (activeStoreCars.value?.store) {
        changePosition('rent', activeStoreCars.value.store as HertzStore)
      }
    })

    const showReturnOrNot = (e: string) => {
      showWindowReturnOrNot.value = e
    }

    const showWindowReturnOrNot = ref<String | null>(null)

    const clearActive = () => emit('update:activeStoreId', null)
    const setActive = (storeCars: StoreCars | HertzStoreCars) => {
      if (carSource === CarSource.GOGOOUT) {
        emit('update:activeStoreId', (storeCars.store as Store).id)
      } else if (showWindowReturnOrNot.value === 'rent' || null) {
        emit('update:activeStoreLocationCode', (storeCars.store as HertzStore).locationCode)
      } else {
        emit('update:activeStoreLocationCode', (storeCars.store as HertzStore).endLocationCode)
      }
    }

    const activeStoreCars = computed({
      get: () => {
        if (carSource === CarSource.GOGOOUT) {
          return filteredStoreCarsList.value?.find(({ store }) =>
            props.activeStoreId === (store as Store).id
        ) ?? null
        } else if (showWindowReturnOrNot.value === 'rent' || null) {
          return filteredStoreCarsList.value?.find(({ store }) =>
            props.activeStoreLocationCode === (store as HertzStore).locationCode
        ) ?? null
        } else {
          return filteredStoreCarsList.value?.find(({ store }) =>
            props.activeStoreLocationCode === (store as HertzStore).endLocationCode
        ) ?? null
        }
      },
      set: val => val,
    })

    const changePosition = (returnOrNot: String, store: HertzStore) => {
      map.value?.setZoom(13)

      if (returnOrNot === 'rent') {
        map.value?.setCenter({ lat: store.coordinate.lat, lng: store.coordinate.lng })
      } else if (store.coordinate.returnLat && store.coordinate.returnLng) {
        map.value?.setCenter({ lat: store.coordinate.returnLat, lng: store.coordinate.returnLng })
      }
    }

    const { app: { store } } = useContext()

    const isGoogleLoaded = computed(() => store?.state.google.isLoaded)

    const isDrawerShow = computed(() => [
      props.activeStoreId != null || props.activeStoreLocationCode != null,
      activeStoreCars.value,
      props.mode === 'drawer',
    ].every(Boolean))

    const bounds = computed(() => {
      const { value: list } = filteredStoreCarsList
      if (!isGoogleLoaded.value || !list?.length) return

      const bounds = new google.maps.LatLngBounds()
      list?.forEach(({ store }) => {
        if (store?.coordinate) bounds.extend(new google.maps.LatLng(store.coordinate))
      })
      return bounds
    })

    const panToCenter = () => {
      const defaultZoom = 14
      if (bounds.value) {
        // 平移至店家中心點
        map.value?.fitBounds(bounds.value)
        map.value?.panToBounds(bounds.value)

        // 調整 zoom 以避免只有一個店家時過度放大
        if (Number(map.value?.getZoom()) > defaultZoom) {
          map.value?.setZoom(defaultZoom)
        }
      } else if (props.origin) {
        // 平移至搜尋點
        map.value?.panTo(props.origin)
        map.value?.setZoom(defaultZoom)
      }
    }
    onMounted(() => watch(bounds, panToCenter, { immediate: true }))

    const filteredStoreCarsList = computed(() =>
      !props.storeCarsList ? [] : props.storeCarsList.filter(storeCars => storeCars.store?.coordinate && storeCars.cars.length)
    )

    const onMarkerClick = (storeCars: StoreCars | HertzStoreCars, event?: PointerEvent) => {
      setActive(storeCars)
      logMapMarkerClick(storeCars, event?.target as HTMLElement)
    }

    // 發送 log
    const logMapZoomIn = () => {
      if (carSource === CarSource.GOGOOUT) {
        ggoLogger.info(LogName.MAP_INTERACT, {
          interact_type: 'zoom-in',
          store_count: filteredStoreCarsList.value.length,
        })
      }
    }
    const onMapZoomIn = _.debounce(logMapZoomIn, 500)

    const logMapZoomOut = () => {
      if (carSource === CarSource.GOGOOUT) {
        ggoLogger.info(LogName.MAP_INTERACT, {
          interact_type: 'zoom-out',
          store_count: filteredStoreCarsList.value.length,
        })
      }
    }
    const onMapZoomOut = _.debounce(logMapZoomOut, 500)

    const logMapDrag = () => {
      if (carSource === CarSource.GOGOOUT) {
        ggoLogger.info(LogName.MAP_INTERACT, {
          interact_type: 'drag',
          store_count: filteredStoreCarsList.value.length,
        })
      }
    }
    const onMapDrag = _.debounce(logMapDrag, 500)

    const logMapMarkerClick = (storeCars: StoreCars | HertzStoreCars, el?: HTMLElement) => {
      if (carSource === CarSource.GOGOOUT && storeCars instanceof StoreCars) {
        ggoLogger.info(LogName.MAP_INTERACT, {
          interact_type: 'marker-click',
          store_id: String(storeCars.store?.id),
          marker_text: el?.innerText,
        })
      }
    }

    watch(() => props.hoverStoreId, (val) => {
      const storeCar = props.storeCarsList.find(storeCar => (storeCar as StoreCars).store?.id === val)
      if (map.value && storeCar) {
        map.value.setCenter({
          lat: storeCar.store?.coordinate.lat!,
          lng: storeCar.store?.coordinate.lng!,
        })
      }
    })

    return {
      el,
      map,
      CarSource,
      carSource,
      returnLocation,
      changePosition,
      isGoogleLoaded,
      showReturnOrNot,
      showWindowReturnOrNot,
      onMarkerClick,
      activeStoreCars,
      isDrawerShow,
      filteredStoreCarsList,

      onInit,

      onMapZoomIn,
      onMapZoomOut,
      onMapDrag,
    }
  },
})
